// Sign-in return target (SEQ-198).
//
// A signed-out visit to a protected page remembers the full target (path,
// query and hash) in sessionStorage. After any successful sign-in the person
// lands there. sessionStorage survives the same-tab Microsoft SSO round trip;
// router state does not.
//
// Every read and write goes through safeNextPath: same-origin relative path
// only. Anything else means "no target" and the app opens at home.

export const RETURN_TARGET_KEY = "seq-claims:login-return";
/** A stored target older than this is ignored and deleted. */
export const RETURN_TARGET_TTL_MS = 10 * 60 * 1000;

/** Minimal storage surface so the logic runs under Deno tests too. */
export interface TargetStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Returns the path when it is a safe same-origin relative path, else null.
 * Must start with exactly one "/". Rejects "//", backslashes, control
 * characters, whitespace, and anything that parses with a scheme or host.
 */
export function safeNextPath(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0 || value.length > 2048) {
    return null;
  }
  if (value[0] !== "/" || value[1] === "/") return null;
  // Backslash, whitespace, control characters (including DEL and C1).
  // eslint-disable-next-line no-control-regex
  if (/[\\\s\u0000-\u001f\u007f-\u009f]/.test(value)) return null;
  if (/^\/*javascript:/i.test(value)) return null;
  // Percent-encoded slash or backslash right after the leading slash can be
  // decoded by some servers into "//" or "/\".
  if (/^\/%(2f|5c)/i.test(value)) return null;
  try {
    const parsed = new URL(value, "http://safe-next.invalid");
    if (parsed.origin !== "http://safe-next.invalid") return null;
  } catch {
    return null;
  }
  return value;
}

/** Paths that must never be stored as a return target. */
export function isReturnableTarget(path: string | null): path is string {
  if (!path) return false;
  const [beforeHash, hash = ""] = [path.split("#")[0], path.split("#").slice(1).join("#")];
  const pathname = beforeHash.split("?")[0].toLowerCase().replace(/\/+$/, "");
  if (pathname === "") return false; // home is the default
  if (pathname === "/login" || pathname === "/complete-setup") return false;
  // One-time OAuth callbacks and anything carrying credentials are never replayed.
  if (pathname.includes("/callback")) return false;
  if (/(^|[?&#])(code|token|access_token|refresh_token|id_token|state)=/i.test(beforeHash.split("?").slice(1).join("?") + "&" + hash)) return false;
  return true;
}

export function storeReturnTarget(
  storage: TargetStorage,
  value: unknown,
  now: number = Date.now(),
): boolean {
  const path = safeNextPath(value);
  if (!isReturnableTarget(path)) return false;
  try {
    storage.setItem(RETURN_TARGET_KEY, JSON.stringify({ path, ts: now }));
    return true;
  } catch {
    return false;
  }
}

/** Reads, validates and DELETES the stored target. Null when none or expired. */
export function consumeReturnTarget(
  storage: TargetStorage,
  now: number = Date.now(),
): string | null {
  let raw: string | null = null;
  try {
    raw = storage.getItem(RETURN_TARGET_KEY);
    storage.removeItem(RETURN_TARGET_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { path?: unknown; ts?: unknown };
    if (typeof parsed.ts !== "number" || now - parsed.ts > RETURN_TARGET_TTL_MS) {
      return null;
    }
    if (parsed.ts > now + 60_000) return null; // clock nonsense
    const path = safeNextPath(parsed.path);
    return isReturnableTarget(path) ? path : null;
  } catch {
    return null;
  }
}

/** Restarts the expiry clock (first-time setup can take longer than ten minutes). */
export function touchReturnTarget(storage: TargetStorage, now: number = Date.now()): void {
  const path = consumeReturnTarget(storage, now);
  if (path) storeReturnTarget(storage, path, now);
}

export function clearReturnTarget(storage: TargetStorage): void {
  try {
    storage.removeItem(RETURN_TARGET_KEY);
  } catch {
    // storage unavailable: nothing to clear
  }
}

// ── Browser bindings ──────────────────────────────────────────

function browserStorage(): TargetStorage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

let deliberateSignOut = false;

/** The sign-out flag stops the route guard re-storing a page the person left on purpose. */
export function markDeliberateSignOut(value: boolean): void {
  deliberateSignOut = value;
}

/** force: an explicit choice (login ?next=, idle timeout) beats the sign-out flag. */
export function rememberReturnTarget(path: string, force = false): void {
  if (deliberateSignOut && !force) return;
  const s = browserStorage();
  if (s) storeReturnTarget(s, path);
}

export function takeReturnTarget(): string | null {
  const s = browserStorage();
  return s ? consumeReturnTarget(s) : null;
}

export function refreshReturnTarget(): void {
  const s = browserStorage();
  if (s) touchReturnTarget(s);
}

export function forgetReturnTarget(): void {
  const s = browserStorage();
  if (s) clearReturnTarget(s);
}
